import { Link, useNavigate } from 'react-router-dom'
import Button from '../../components/common/button'
import IconBox from '../../components/common/icon-box'
import MainNav from '../../components/homepage/main-nav'
import Layout from '../../components/layout/layout'
import { SubmitHandler, useForm } from 'react-hook-form'
import { ForgotPasswordFinishDto } from '@ringo/shared'
import { useEffect, useState } from 'react'
import { useUser } from '../../common/hooks/auth/useUser'
import { useTranslation } from '../../useTranslation'
import { useMutation } from '@tanstack/react-query'
import ControlledInputText from '../../components/form/controlled-input-text'
import { object, string, ref } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useValidationRules } from '../../common/validation-rules'
import InfoMessage from '../../components/common/info-message'
import { Validation } from '../../common/types'
import { useIamApi } from '../../common/api-context'
import InvalidToken from './invalid-token'
import Loader from '../../components/common/Loader'

const ConfirmPasswordChange = () => {
    const navigate = useNavigate()
    const { isAuthenticated, extractTokenFromUrl } = useUser()
    const { t } = useTranslation()
    const iamApi = useIamApi()
    const validationRules = useValidationRules()

    // Redirect if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated, navigate])

    const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null)

    useEffect(() => {
        const token = extractTokenFromUrl()

        iamApi
            .isTokenValid({ token: token! })
            .then((response) => setIsTokenValid(response.data && !response.data.expired && !response.data.invalid && !response.data.used))
    }, [])

    const validationObject: Validation<ForgotPasswordFinishDto> = {
        newPassword: validationRules.password,
        confirmNewPassword: string()
            .required()
            .oneOf([ref('newPassword')], { key: 'createAccount.passwordsDontMatch' }),
    }

    const schema = object(validationObject)
    const { handleSubmit, setValue, control } = useForm<ForgotPasswordFinishDto>({
        resolver: yupResolver(schema as any),
    })

    useEffect(() => {
        setValue('token', extractTokenFromUrl() as string)
    }, [])

    const confirmPasswordChange = useMutation({
        mutationFn: (formData: ForgotPasswordFinishDto) => {
            return iamApi.forgotPasswordFinish(formData)
        },
    })

    const onSubmit: SubmitHandler<ForgotPasswordFinishDto> = async (data) => {
        confirmPasswordChange.mutate(data)
    }

    return (
        <Layout>
            <MainNav />
            <div className="mx-auto my-16 w-3/6 max-tablet:w-2/3 ">
                {isTokenValid == null ? (
                    <Loader></Loader>
                ) : isTokenValid === true ? (
                    <IconBox icon="key">
                        <div className="py-10 px-2">
                            {confirmPasswordChange.isSuccess ? (
                                <div>
                                    <InfoMessage type="success" label={t('login.passwordUpdateSuccess')} icon="check" />
                                    <div className="text-center -mb-4 flex gap-4 justify-center">
                                        <Link to="/">
                                            <Button text={t('backToRingo')} type="squareYellow" />
                                        </Link>
                                        <Link to="/auth/login">
                                            <Button text={t('login')} type="squareBlue" />
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit(onSubmit)} className="">
                                    <p className="font-bold text-xl text-left mb-6 leading-0 max-tablet:text-base">{t('changePassword')}</p>
                                    <div>
                                        <ControlledInputText
                                            label={t('myAccount.personalData.newPassword')}
                                            control={control}
                                            name="newPassword"
                                            required
                                            type="password"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <ControlledInputText
                                            label={t('myAccount.repeatNewPassword')}
                                            control={control}
                                            name="confirmNewPassword"
                                            required
                                            type="password"
                                        />
                                    </div>
                                    <div className="flex justify-center mt-7 gap-3">
                                        <Link to="/">
                                            <Button icon="xmark" text={t('cancel')} type={'squareBlue'} preventSubmit />
                                        </Link>
                                        <Button icon="envelope" text={t('changePassword')} type={'squareYellow'} isLoading={confirmPasswordChange.isPending} />
                                    </div>
                                </form>
                            )}
                        </div>
                    </IconBox>
                ) : (
                    <InvalidToken />
                )}
            </div>
        </Layout>
    )
}

export default ConfirmPasswordChange
